import type { IndividualPaymentPersonCourseViewModel } from '@/shared/api/generated';

import type { IndividualPaymentContractAction } from '../types/individualPayment';
import { FINANCIAL_PERSON_STATUS_INACTIVE } from '../types/individualPayment';

/**
 * Papéis do operador que as regras do grid legado de contratos consultam.
 *
 * Só `isSecretary` entra: diferente das parcelas, nenhuma ação do contrato é
 * exclusiva de `Master` — e `Master` também **não** ignora a barreira de
 * secretária, igual ao que `isParcelOperationAvailable` já faz.
 */
export interface ContractViewer {
  /** `Secretary` não escreve: é o papel que esconde as três ações de escrita. */
  readonly isSecretary: boolean;
}

/**
 * A ação deve aparecer no menu "Gerenciar" deste contrato?
 *
 * As regras vêm dos botões do grid legado (`IndividualPayment/Index`), onde cada
 * ícone da célula tinha a própria condição de render:
 *
 * | Ação | Condição do legado |
 * | --- | --- |
 * | `paymentConsolidation` | `dataItem.IsUSP == true` — sem condição de papel |
 * | `editIndividualPayment` | `!isSecretary` |
 * | `viewParcels` | sempre visível, inclusive para secretária |
 * | `deactivateFinancial` | `!isSecretary` **e** `StatusId != Inactive` |
 * | `addIndividualPayment` | `!isSecretary` |
 *
 * **`isUSP === true`, não `!== false`.** O campo é opcional no view model, e o
 * legado testa a igualdade sobre um `bool`. Exigir o `true` explícito mantém o
 * lado fail-closed: contrato cuja resposta não trouxe a flag não oferece a
 * consolidação, em vez de oferecê-la por omissão.
 *
 * @param action - Item do menu do contrato.
 * @param contract - Linha da grid, de onde saem `isUSP` e `statusId`.
 * @param viewer - Papéis do operador logado.
 * @returns `true` quando o item deve ser renderizado.
 *
 * @example
 * ```ts
 * isContractActionAvailable('paymentConsolidation', contract, { isSecretary: false });
 * ```
 */
export function isContractActionAvailable(
  action: IndividualPaymentContractAction,
  contract: Pick<IndividualPaymentPersonCourseViewModel, 'isUSP' | 'statusId'>,
  viewer: ContractViewer,
): boolean {
  // As duas ações sem condição de papel ficam antes da barreira de secretária.
  if (action === 'viewParcels') return true;

  // A consolidação é leitura de um demonstrativo: o legado a libera para
  // qualquer papel, e restringe pela natureza do contrato.
  if (action === 'paymentConsolidation') return contract.isUSP === true;

  if (viewer.isSecretary) return false;

  // Contrato já inativado não oferece a própria inativação.
  if (action === 'deactivateFinancial') {
    return contract.statusId !== FINANCIAL_PERSON_STATUS_INACTIVE;
  }

  return true;
}
